<template>
  <section v-if="loading || hasData" class="sector-benchmark-metrics">
    <page-sub-header title="Business Metrics" class="mt-4" />
    <sme-card>
      <app-loading v-if="loading" />
      <template v-else>
        <p class="mb-3 text-muted">
          Comparing your engagement metrics against the <strong>{{ sector }}</strong> sector average.
        </p>
        <b-table :items="metrics" :fields="TABLE_FIELDS" class="mb-0" table-class="table-minimal" responsive show-empty>
          <template #cell(name)="{ item }">
            {{ item.name }}
            <span :id="`benchmark-info-${item.id}`" class="info-icon">
              <font-awesome-icon :icon="['fas', 'info-circle']" />
            </span>
            <b-tooltip :target="`benchmark-info-${item.id}`" placement="top">
              {{ item.description }}
            </b-tooltip>
          </template>
          <template #cell(company_value)="{ item }">
            <span class="metric-value">{{ formatValue(item.company_value, item.unit) }}</span>
          </template>
          <template #cell(sector_value)="{ item }">
            <span class="metric-value">{{ formatValue(item.sector_value, item.unit) }}</span>
          </template>
        </b-table>
      </template>
    </sme-card>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';

const TABLE_FIELDS = [
  { key: 'name', label: 'Metric' },
  { key: 'company_value', label: 'Your Company' },
  { key: 'sector_value', label: 'Sector Average' },
];

const loading = ref(true);
const sector = ref(null);
const metrics = ref([]);

const hasData = computed(() => sector.value && metrics.value.length > 0);

const formatValue = (value, unit) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const num = Number(value);
  if (isNaN(num)) {
    return '-';
  }
  switch (unit) {
    case 'currency':
      return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'decimal':
      return num.toFixed(2);
    case 'months':
      return `${num.toFixed(1)} months`;
    case 'days':
      return `${num.toFixed(1)} days`;
    case 'percent':
      return `${(Math.round(num * 10) / 10).toFixed(1)}%`;
    default:
      return String(num);
  }
};

const fetchData = async () => {
  loading.value = true;

  try {
    const data = await ApiClient.getSectorBenchmarks();
    sector.value = data?.sector || null;
    metrics.value = data?.metrics || [];
  } catch (error) {
    console.error('Failed to fetch sector benchmarks:', error);
  }

  loading.value = false;
};

onBeforeMount(() => fetchData());
</script>

<style lang="scss" scoped>
.sector-benchmark-metrics {
  .metric-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--palette-color-brand-primary);
  }

  .info-icon {
    margin-left: 0.35rem;
    color: var(--palette-color-default-lighten-20);
    cursor: help;
  }
}
</style>
